import * as React from "react";
import moment from "moment";
import {
  ICoachPerformanceResponse,
  ICoachPerformanceError,
  ICoach
} from "./coach-performance.types";

const API_URL = process.env.REACT_APP_COACH_PERF_API_URL || "";
const API_KEY = process.env.REACT_APP_COACH_PERF_API_KEY || "";
const DEFAULT_SUBDOMAIN =
  process.env.REACT_APP_COACH_PERF_DEFAULT_SUBDOMAIN || "";

export interface IUseCoachPerformance {
  /** The anchor month's response (the month visible in the picker). */
  data: ICoachPerformanceResponse | null;
  /** Anchor + 2 trailing months for 90-day rolling averages. */
  trailing: ICoachPerformanceResponse[];
  error: ICoachPerformanceError | null;
  loading: boolean;
  configured: boolean;
  subdomain: string;
  month: string;
  setSubdomain: (s: string) => void;
  setMonth: (m: string) => void;
  refetch: () => void;
}

export const ALLOWED_SUBDOMAINS = [
  "startingstrengthcentennial",
  "startingstrengthsunsetvalley",
  "startingstrengthaustin"
];

/** Pretty display name for a gym subdomain. Falls back to subdomain. */
export function gymDisplayName(
  subdomain: string,
  gymName?: string | null
): string {
  if (gymName && gymName.trim().length) return gymName;
  const map: Record<string, string> = {
    startingstrengthcentennial: "Centennial",
    startingstrengthsunsetvalley: "Sunset Valley",
    // Subdomain is "austin" but the operational name everyone uses is
    // "West Anderson" (per the GHL config and the PDF reports).
    startingstrengthaustin: "West Anderson"
  };
  return map[subdomain] || subdomain;
}

/** "April 2026" from "2026-04". */
export function monthDisplayName(monthYm: string): string {
  return moment(`${monthYm}-01`).format("MMMM YYYY");
}

export function currentMonth(): string {
  return moment().format("YYYY-MM");
}

/** Returns [anchor, anchor−1, anchor−2, ...] in YYYY-MM. */
export function trailingMonths(anchor: string, count: number): string[] {
  const out: string[] = [];
  const base = moment(`${anchor}-01`);
  for (let i = 0; i < count; i += 1) {
    out.push(
      base
        .clone()
        .subtract(i, "month")
        .format("YYYY-MM")
    );
  }
  return out;
}

/** One-shot fetch. Throws an ICoachPerformanceError on failure. */
async function fetchCoachPerformance(
  subdomain: string,
  month: string
): Promise<ICoachPerformanceResponse> {
  const url = `${API_URL}?subdomain=${encodeURIComponent(
    subdomain
  )}&month=${encodeURIComponent(month)}`;
  const res = await fetch(url, { headers: { "X-API-Key": API_KEY } });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const errBody: ICoachPerformanceError = body || {
      error: "UNKNOWN_ERROR",
      message: `HTTP ${res.status}`
    };
    throw errBody;
  }
  return body as ICoachPerformanceResponse;
}

export function useCoachPerformance(): IUseCoachPerformance {
  const configured = Boolean(API_URL && API_KEY);

  const [subdomain, setSubdomain] = React.useState<string>(
    DEFAULT_SUBDOMAIN || ALLOWED_SUBDOMAINS[0]
  );
  const [month, setMonth] = React.useState<string>(currentMonth());
  const [data, setData] = React.useState<ICoachPerformanceResponse | null>(
    null
  );
  const [trailing, setTrailing] = React.useState<ICoachPerformanceResponse[]>(
    []
  );
  const [error, setError] = React.useState<ICoachPerformanceError | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [reloadKey, setReloadKey] = React.useState(0);

  React.useEffect(() => {
    if (!configured) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);

    // 3-month window for rolling averages (anchor + 2 prior months).
    const months = trailingMonths(month, 3);

    Promise.allSettled(
      months.map(m => fetchCoachPerformance(subdomain, m))
    ).then(results => {
      if (cancelled) return;

      const fulfilled = results
        .map(r => (r.status === "fulfilled" ? r.value : null))
        .filter(v => v !== null) as ICoachPerformanceResponse[];

      const anchorResult = results[0];

      if (anchorResult.status === "rejected") {
        const reason = anchorResult.reason as
          | ICoachPerformanceError
          | Error
          | undefined;
        const normalized: ICoachPerformanceError =
          reason && (reason as ICoachPerformanceError).error
            ? (reason as ICoachPerformanceError)
            : {
                error: "FETCH_FAILED",
                message:
                  ((reason as Error) && (reason as Error).message) ||
                  "Network error"
              };
        setError(normalized);
        setData(null);
        setTrailing([]);
        setLoading(false);
        return;
      }

      setData(anchorResult.value);
      setTrailing(fulfilled);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [configured, subdomain, month, reloadKey]);

  const refetch = React.useCallback(() => setReloadKey(k => k + 1), []);

  return {
    data,
    trailing,
    error,
    loading,
    configured,
    subdomain,
    setSubdomain,
    month,
    setMonth,
    refetch
  };
}

/* ---------- formatting helpers ---------- */

export function formatCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "—";
  const dollars = cents / 100;
  return dollars.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
  });
}

export function formatRate(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return "—";
  return `${Math.round(rate * 100)}%`;
}

export function formatNumber(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString("en-US");
}

/* ---------- "All coaches" aggregate (admin view) ---------- */

export const ALL_COACHES_ID = -1;

/**
 * Build a synthetic ICoach row from the API's gym-wide `totals` block
 * plus a deduplicated union of every coach's members[]. Used for the
 * admin "All" option in the Coach picker so the per-coach KPI rows on
 * the dashboard render gym-wide numbers without double-counting shared
 * members.
 *
 * For members that appear in multiple coaches' rosters (split attribution),
 * we sum mrr_cents/collected_cents/expected_cents/visits/days_assigned so
 * the roster line reflects the member's full participation, and prefer
 * tier_1 over tier_2 when picking the attribution badge.
 */
export function buildAllCoachesAggregate(
  data: ICoachPerformanceResponse
): ICoach {
  const t = (data.totals as unknown) as Record<string, unknown>;
  const memberMap = new Map<number, ICoach["members"][number]>();

  data.coaches.forEach(c => {
    (c.members || []).forEach(m => {
      const existing = memberMap.get(m.person_id);
      if (!existing) {
        memberMap.set(m.person_id, { ...m });
        return;
      }
      existing.mrr_cents = (existing.mrr_cents || 0) + (m.mrr_cents || 0);
      existing.collected_cents =
        (existing.collected_cents || 0) + (m.collected_cents || 0);
      existing.expected_cents =
        (existing.expected_cents || 0) + (m.expected_cents || 0);
      existing.visits_count =
        (existing.visits_count || 0) + (m.visits_count || 0);
      existing.days_assigned =
        (existing.days_assigned || 0) + (m.days_assigned || 0);
      if (m.attribution_tier === "tier_1") {
        existing.attribution_tier = "tier_1";
      }
    });
  });

  const pickNumber = (k: string): number =>
    typeof t[k] === "number" ? (t[k] as number) : 0;
  const pickNumberOrNull = (k: string): number | null =>
    typeof t[k] === "number" ? (t[k] as number) : null;

  return {
    person_id: ALL_COACHES_ID,
    name: "All coaches",
    mrr_cents: pickNumber("mrr_cents"),
    collected_cents: pickNumber("collected_cents"),
    total_members: pickNumber("total_members"),
    in_person_members: pickNumber("in_person_members"),
    online_members: pickNumber("online_members"),
    booked_slots: pickNumber("booked_slots"),
    attended_slots: pickNumber("attended_slots"),
    attendance_rate: pickNumberOrNull("attendance_rate"),
    cancellations: pickNumber("cancellations"),
    holds: pickNumber("holds"),
    churn_rate: pickNumberOrNull("churn_rate"),
    free_session_metrics: t.free_session_metrics as ICoach["free_session_metrics"],
    free30_process_metrics: t.free30_process_metrics as ICoach["free30_process_metrics"],
    contact_form_submission_count: pickNumber("contact_form_submission_count"),
    contacted_working_count: pickNumber("contacted_working_count"),
    committed_count: pickNumber("committed_count"),
    closed_won_count: pickNumber("closed_won_count"),
    open_opportunities_total: pickNumber("open_opportunities_total"),
    on_hold_count: pickNumber("on_hold_count"),
    win_back_eligible_count: pickNumber("win_back_eligible_count"),
    win_back_30d_count: pickNumber("win_back_30d_count"),
    win_back_60d_count: pickNumber("win_back_60d_count"),
    win_back_90d_count: pickNumber("win_back_90d_count"),
    alumni_count: pickNumber("alumni_count"),
    no_win_back_count: pickNumber("no_win_back_count"),
    late_tasks_count: pickNumber("late_tasks_count"),
    members_no_contact_30d_count: pickNumber("members_no_contact_30d_count"),
    new_referrals_count: pickNumber("new_referrals_count"),
    members: Array.from(memberMap.values())
  };
}

/* ---------- Row 3 compliance tile builder (API-fed) ---------- */

export interface IComplianceTileRow {
  label: string;
  rate: string;
  detail: string;
}

/**
 * Build Row 3 tiles from the server-side free30_process_metrics block.
 * Replaces the old direct-GHL hook. Specificity stays "—" because the
 * API explicitly does not judge it (see API metric_notes).
 */
export function buildComplianceFromCoachApi(
  coach: ICoach | null
): { tiles: IComplianceTileRow[]; assigned: number } {
  const m = coach?.free30_process_metrics;
  const assigned = m?.free30_leads_assigned ?? 0;

  const pct = (n: number): string =>
    assigned === 0 ? "—" : `${Math.round((n / assigned) * 100)}%`;

  const tiles: IComplianceTileRow[] = [
    {
      label: "Response < 24h",
      rate: pct(m?.free30_leads_with_manual_reply_pre_session ?? 0),
      detail:
        m && assigned > 0
          ? `${
              m.free30_leads_with_manual_reply_pre_session
            }/${assigned} · manual reply pre-session${
              m.median_response_minutes_first_inbound_to_first_manual !== null
                ? ` · median ${m.median_response_minutes_first_inbound_to_first_manual} min`
                : ""
            }`
          : "Coach manual reply between booking and session"
    },
    {
      label: "Specificity",
      rate: formatRate(m?.free_session_specificity_rate ?? null),
      detail:
        m && assigned > 0
          ? `${m.free_session_specificity_count}/${assigned} guests had specific replies · LLM-graded server-side`
          : "Pre-session reply named guest-specific details"
    },
    {
      label: "Video Included",
      rate: pct(m?.free30_leads_with_manual_pre_session_url ?? 0),
      detail:
        m && assigned > 0
          ? `${m.free30_leads_with_manual_pre_session_url}/${assigned} · URL presence (not video-judged)`
          : "Pre-session message contained any URL"
    },
    {
      label: "Pre-Session Call",
      rate: "—",
      detail:
        m && assigned > 0
          ? `${m.pre_session_calls_placed_count} calls across ${assigned} guests · API gives totals, not per-guest`
          : "Voice contact before session"
    },
    {
      label: "Notes Added",
      rate: pct(m?.free30_leads_with_notes_added ?? 0),
      detail:
        m && assigned > 0
          ? `${m.free30_leads_with_notes_added}/${assigned} · notes recorded`
          : "Notes added to Member Relationships between booking and session"
    }
  ];

  return { tiles, assigned };
}

/** Tier breakdown computed from a coach's members[]. */
export function tierBreakdown(
  coach: ICoach | null
): {
  tier1: number;
  tier2: number;
} {
  if (!coach) return { tier1: 0, tier2: 0 };
  const members = coach.members || [];
  const tier1 = members.filter(m => m.attribution_tier === "tier_1").length;
  const tier2 = members.filter(m => m.attribution_tier === "tier_2").length;
  return { tier1, tier2 };
}

/* ---------- action-item filters over coach.members[] ---------- */

/** Days between a YYYY-MM-DD string and today, ignoring TZ. Negative = future. */
export function daysSince(date: string | null | undefined): number | null {
  if (!date) return null;
  const d = moment(date, "YYYY-MM-DD");
  if (!d.isValid()) return null;
  return moment()
    .startOf("day")
    .diff(d.startOf("day"), "days");
}

export interface ILongHold {
  name: string;
  holdStart: string;
  days: number;
}

export interface ICancellation {
  name: string;
  cancelDate: string;
  plan: string | null;
}

export interface IInvoiceDue {
  name: string;
  due: string;
  daysUntil: number;
  mrrCents: number;
}

/** Members on hold for >= minDays. */
export function membersOnLongHold(
  coach: ICoach | null,
  minDays = 60
): ILongHold[] {
  if (!coach) return [];
  return (coach.members || [])
    .map(m => {
      if (!m.hold_start) return null;
      const days = daysSince(m.hold_start);
      if (days === null || days < minDays) return null;
      return { name: m.name, holdStart: m.hold_start, days };
    })
    .filter(h => h !== null)
    .map(h => h as ILongHold)
    .sort((a, b) => b.days - a.days);
}

/** Members with cancel_date within the displayed month (YYYY-MM). */
export function cancellationsInMonth(
  coach: ICoach | null,
  monthYm: string
): ICancellation[] {
  if (!coach) return [];
  return (coach.members || [])
    .filter(m => m.cancel_date && m.cancel_date.startsWith(monthYm))
    .map(m => ({
      name: m.name,
      cancelDate: m.cancel_date as string,
      plan: m.plan_name
    }))
    .sort((a, b) => a.cancelDate.localeCompare(b.cancelDate));
}

/** Find the same coach (by person_id) across multiple monthly responses. */
export function coachAcrossMonths(
  personId: number | null | undefined,
  months: ICoachPerformanceResponse[]
): ICoach[] {
  if (!personId) return [];
  return months
    .map(m => m.coaches.find(c => c.person_id === personId))
    .filter(c => c !== undefined)
    .map(c => c as ICoach);
}

/** Average a numeric field across coaches, ignoring null/undefined. */
export function averageField(
  coaches: ICoach[],
  field: keyof ICoach
): number | null {
  const values = coaches
    .map(c => c[field])
    .filter(v => typeof v === "number") as number[];
  if (values.length === 0) return null;
  const sum = values.reduce((a, b) => a + b, 0);
  return sum / values.length;
}

/** Average using an arbitrary accessor — for nested fields like
 *  free_session_metrics. */
export function averageBy(
  coaches: ICoach[],
  accessor: (c: ICoach) => number | null | undefined
): number | null {
  const values = coaches
    .map(accessor)
    .filter(v => typeof v === "number")
    .map(v => v as number);
  if (values.length === 0) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

/* ---------- nested-field accessors (free_session_metrics) ----------
 * The Coach Performance API moved the free-session funnel under a nested
 * block. All reads should go through these helpers so a future contract
 * change is one file to update.
 */

export function getShowRate(c: ICoach): number | null {
  return c.free_session_metrics?.pike13_calendar_show_rate ?? null;
}

export function getConversionRate(c: ICoach): number | null {
  return c.free_session_metrics?.pike13_calendar_conversion_rate ?? null;
}

export function getFreeSessionsBooked(c: ICoach): number {
  return c.free_session_metrics?.pike13_calendar_bookings_this_month ?? 0;
}

export function getFreeSessionsAttended(c: ICoach): number {
  return c.free_session_metrics?.pike13_calendar_attended_this_month ?? 0;
}

export function getFreeSessionsConverted(c: ICoach): number {
  return c.free_session_metrics?.pike13_calendar_converted_this_month ?? 0;
}

/* ---------- live pricing derivation from member MRRs ---------- */

export interface ILivePricingRow {
  planName: string;
  monthlyCents: number;
  memberCount: number;
  membersAtThisPrice: number;
  /** Set when the row comes from the Pike13 catalog, not MRR derivation. */
  cadence?: string;
}

/**
 * Aggregate plan pricing from the gym's current paying members. Modal
 * MRR per plan_name is the "headline price" (the rate most members
 * actually pay). Filters out $0 MRR (comps, promos) and non-membership
 * plan types.
 */
export function derivePricing(coaches: ICoach[]): ILivePricingRow[] {
  const byPlan = new Map<string, number[]>();
  coaches.forEach(c => {
    (c.members || []).forEach(m => {
      if (!m.plan_name) return;
      if (m.plan_type !== "membership") return;
      if (!m.mrr_cents) return;
      const arr = byPlan.get(m.plan_name) ?? [];
      arr.push(m.mrr_cents);
      byPlan.set(m.plan_name, arr);
    });
  });

  const rows: ILivePricingRow[] = [];
  byPlan.forEach((mrrs, planName) => {
    const counts = new Map<number, number>();
    mrrs.forEach(v => counts.set(v, (counts.get(v) ?? 0) + 1));
    let modePrice = 0;
    let modeCount = 0;
    counts.forEach((cnt, price) => {
      if (cnt > modeCount) {
        modeCount = cnt;
        modePrice = price;
      }
    });
    rows.push({
      planName,
      monthlyCents: modePrice,
      memberCount: mrrs.length,
      membersAtThisPrice: modeCount
    });
  });

  return rows.sort((a, b) => b.memberCount - a.memberCount);
}

/** Members whose invoice_due_date is within the next `withinDays`. */
export function invoicesDueSoon(
  coach: ICoach | null,
  withinDays = 7
): IInvoiceDue[] {
  if (!coach) return [];
  return (coach.members || [])
    .map(m => {
      if (!m.invoice_due_date) return null;
      const daysSinceDue = daysSince(m.invoice_due_date);
      if (daysSinceDue === null) return null;
      const daysUntil = -daysSinceDue;
      if (daysUntil < 0 || daysUntil > withinDays) return null;
      return {
        name: m.name,
        due: m.invoice_due_date,
        daysUntil,
        mrrCents: m.mrr_cents
      };
    })
    .filter(d => d !== null)
    .map(d => d as IInvoiceDue)
    .sort((a, b) => a.daysUntil - b.daysUntil);
}
